import { Link } from "react-router-dom";
import { Pencil } from "lucide-react";
import { REVENUE_BANDS, CAPITAL_PURPOSE_LABEL } from "../../lib/quizFunderPreview";

const RAISE_BRACKET_LABEL = {
  under_1m: "Under KES 1M",
  "1m_5m": "KES 1M – 5M",
  "5m_15m": "KES 5M – 15M",
  "15m_25m": "KES 15M – 25M",
  "25m_50m": "KES 25M – 50M",
  "50m_100m": "KES 50M – 100M",
  "100m_500m": "KES 100M – 500M",
  over_500m: "Over KES 500M",
};

const PROFIT_LABEL = { yes: "Profitable", no: "Not yet profitable", not_sure: "Not sure yet" };
const INDUSTRY_LABEL = {
  agriculture: "Agriculture / AgriTech", healthcare: "Healthcare / HealthTech",
  financial: "Financial Services / FinTech", manufacturing: "Manufacturing / Industrials",
  retail: "Retail / Consumer Goods", logistics: "Logistics & Supply Chain",
  energy: "Energy / CleanTech", real_estate: "Real Estate / Construction",
  tech_saas: "Technology / SaaS", education: "Education / EdTech",
  hospitality: "Hospitality / Tourism", other: "Other",
};

// Read-only recap of the founder's Get Started quiz answers, shown at the
// top of wizard Step 1 so onboarding feels like a continuation of the quiz.
export const QuizSnapshotCard = ({ quiz, purposes = [] }) => {
  if (!quiz) return null;
  const rows = [];
  if (quiz.stage) {
    rows.push(["Business stage", quiz.stage === "pre_revenue" ? "Pre-revenue" : "Post-revenue"]);
  }
  if (quiz.industry) {
    rows.push(["Industry", quiz.industry === "other" && quiz.industry_other
      ? quiz.industry_other : (INDUSTRY_LABEL[quiz.industry] || quiz.industry)]);
  }
  const band = REVENUE_BANDS.find((b) => b.id === quiz.revenue_band);
  if (band) rows.push(["Monthly revenue", band.label.replace(" per month", "")]);
  if (quiz.is_profitable && PROFIT_LABEL[quiz.is_profitable]) {
    rows.push(["Profitability", PROFIT_LABEL[quiz.is_profitable]]);
  }
  const primary = quiz.capital_purpose_primary;
  const plist = purposes.length ? purposes : (quiz.capital_purposes || []);
  if (plist.length) {
    const ordered = primary && plist.includes(primary)
      ? [primary, ...plist.filter((p) => p !== primary)] : plist;
    rows.push(["Use of funds", ordered.map((p, i) =>
      (CAPITAL_PURPOSE_LABEL[p] || p) + (i === 0 && primary ? " (primary)" : "")).join(", ")]);
  }
  if (quiz.capital_purpose_other) rows.push(["Other capital need", quiz.capital_purpose_other]);
  if (RAISE_BRACKET_LABEL[quiz.raise_amount_bracket]) {
    rows.push(["Raising", RAISE_BRACKET_LABEL[quiz.raise_amount_bracket]]);
  }
  if (!rows.length) return null;
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-5" data-testid="quiz-snapshot-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-bold uppercase tracking-wider text-[#0A8B85]">Your Business Snapshot</p>
          <p className="mt-0.5 text-xs text-neutral-500">From your Get Started quiz — we'll use this throughout your journey.</p>
        </div>
        <Link to="/get-started" data-testid="quiz-snapshot-edit"
          className="inline-flex items-center gap-1 text-xs font-semibold text-[#0A8B85] hover:underline">
          <Pencil className="h-3 w-3" /> Edit answers
        </Link>
      </div>
      <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-baseline gap-2" data-testid={`quiz-snapshot-row-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <dt className="w-32 shrink-0 text-xs font-semibold text-neutral-500">{label}</dt>
            <dd className="text-sm font-semibold text-neutral-900">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};
